import { UNGROUPED_RESOURCES_GROUP_ID } from '@/console/features/groups/components/group-list/group-list.constants';
import { api } from '@/shared/redux/api/_generated/api/jobs-api.generated';
import { TRtkApiTags } from '@/shared/types/api.types';

type TJobsApiTags = 'Pipeline' | 'DeploymentRequest';

export const jobsApi = api.enhanceEndpoints({
  addTagTypes: ['Pipeline', 'Job', 'Groups', 'DeploymentRequest'],
  endpoints: {
    ReadJobsByDeviceIds: {
      providesTags: [{ type: 'Job', id: 'LIST' }],
    },
    ReadJobsByProjectId: {
      providesTags: [{ type: 'Job', id: 'LIST' }],
    },
    ReadJobsByProjectIds: {
      providesTags: [{ type: 'Job', id: 'LIST' }],
    },
    ReadLatestJobsByProjectIds: {
      providesTags: [{ type: 'Job', id: 'LIST' }],
    },
    ReadVersionOriginJobs: {
      providesTags: [{ type: 'Job', id: 'LIST' }],
    },
    CreateConversion: {
      invalidatesTags: [{ type: 'Job', id: 'LIST' }],
    },
    ReadDeploymentRequests: {
      providesTags: (result) => {
        const tags: TRtkApiTags<TJobsApiTags> = [
          { type: 'DeploymentRequest', id: 'LIST' },
        ];
        result?.readDeploymentRequests.items.forEach((request) => {
          tags.push({
            type: 'DeploymentRequest',
            id: request.deploymentRequestId,
          });
        });
        return tags;
      },
    },
    ReadDeploymentRequest: {
      providesTags: (_, __, args) => [
        { type: 'DeploymentRequest', id: args.deploymentRequestId },
      ],
    },
    CreateDeploymentRequest: {
      invalidatesTags: [{ type: 'DeploymentRequest', id: 'LIST' }],
    },
    ApproveDeploymentRequest: {
      invalidatesTags: (_, __, args) => [
        { type: 'DeploymentRequest', id: args.deploymentRequestId },
        { type: 'DeploymentRequest', id: 'LIST' },
      ],
    },
    DenyDeploymentRequest: {
      invalidatesTags: (_, __, args) => [
        { type: 'DeploymentRequest', id: args.deploymentRequestId },
        { type: 'DeploymentRequest', id: 'LIST' },
      ],
    },
    CancelDeploymentRequest: {
      invalidatesTags: (_, __, args) => [
        { type: 'DeploymentRequest', id: args.deploymentRequestId },
        { type: 'DeploymentRequest', id: 'LIST' },
      ],
    },
    ExecuteDeploymentRequest: {
      // Execution creates a job, so the device's job list is stale too.
      invalidatesTags: (_, __, args) => [
        { type: 'DeploymentRequest', id: args.deploymentRequestId },
        { type: 'DeploymentRequest', id: 'LIST' },
        { type: 'Job', id: 'LIST' },
      ],
    },
    DeleteDeploymentRequest: {
      invalidatesTags: (_, __, args) => [
        { type: 'DeploymentRequest', id: args.deploymentRequestId },
        { type: 'DeploymentRequest', id: 'LIST' },
      ],
    },
    ReadAllPipelines: {
      providesTags: (result, _, args) => {
        const tags: TRtkApiTags<TJobsApiTags> = [
          { type: 'Pipeline', id: 'LIST' },
        ];

        result?.readAllPipelineConfigurations.items.forEach((pipeline) => {
          tags.push({
            type: 'Pipeline',
            id: pipeline.pipelineConfigurationId,
          });
        });

        const filteredGroup = args?.filter?.groupId;
        const groupId = filteredGroup?.contains ?? UNGROUPED_RESOURCES_GROUP_ID;
        if (filteredGroup) {
          return [...tags, { type: 'Groups' as const, id: groupId }];
        }

        return tags;
      },
    },
    CreatePipelineConfiguration: {
      invalidatesTags: [{ type: 'Pipeline', id: 'LIST' }, 'Groups' as const],
    },
    UpdatePipelineConfiguration: {
      invalidatesTags: (_, __, arg) => [
        { type: 'Pipeline' as const, id: arg.pipelineConfigurationId },
        { type: 'Pipeline', id: 'LIST' },
        'Groups' as const,
      ],
    },
    DeletePipelineConfiguration: {
      invalidatesTags: [{ type: 'Pipeline', id: 'LIST' }, 'Groups' as const],
    },
    ReadPipeline: {
      providesTags: (_, __, args) => {
        return [{ type: 'Pipeline', id: args.pipelineConfigurationId }];
      },
    },
    TriggerPipelineExecution: {
      invalidatesTags: (_, __, args) => {
        return [{ type: 'Pipeline', id: args.pipelineConfigurationId }];
      },
    },
    ReadPipelineExecutionsForPipeline: {
      providesTags: (_, __, args) => {
        return [{ type: 'Pipeline', id: args.pipelineConfigurationId }];
      },
    },
    ReadLatestExecutionsForPipelines: {
      providesTags: (result) =>
        (result?.readLatestExecutionsForPipelines ?? []).map((execution) => ({
          type: 'Pipeline' as const,
          id: execution.pipelineConfigurationId,
        })),
    },
    ReadGroupedPipelineCounts: {
      providesTags: (result) =>
        (result?.readGroupedPipelineCounts ?? []).map((count) => ({
          type: 'Groups' as const,
          id: count.groupId,
        })),
    },
    ReadJob: {
      providesTags: (_, __, args) => {
        return [{ type: 'Job', id: args.jobId }];
      },
    },
    ReadJobLogs: {
      providesTags: (_, __, args) => {
        return [{ type: 'Job', id: args.jobId }];
      },
    },
    DeleteJob: {
      invalidatesTags: [{ type: 'Job', id: 'LIST' }],
    },
    DeletePipelineExecution: {
      invalidatesTags: (result, _, __) => {
        const pipelineConfigurationId =
          result?.deletePipelineExecution.pipelineConfigurationId;
        if (!pipelineConfigurationId) {
          return [];
        }
        return [{ type: 'Pipeline', id: pipelineConfigurationId }];
      },
    },
    CreateDeviceJob: {
      invalidatesTags: [{ type: 'Job', id: 'LIST' }],
    },
    CreateJob: {
      invalidatesTags: [{ type: 'Job', id: 'LIST' }],
    },
    ReadPipelineExecution: {
      providesTags: (result, _, __) => {
        const pipelineConfigurationId =
          result?.readPipelineExecution?.pipelineConfigurationId;
        if (!pipelineConfigurationId) {
          return [];
        }
        return [{ type: 'Pipeline', id: pipelineConfigurationId }];
      },
    },
  },
});

export const {
  useReadAllPipelinesQuery,
  useReadPipelineExecutionsForPipelineQuery,
  useLazyReadPipelineExecutionsForPipelineQuery,
  useCreatePipelineConfigurationMutation,
  useDeletePipelineConfigurationMutation,
  useReadJobsByDeviceIdsQuery,
  useLazyReadJobsByDeviceIdsQuery,
  useReadJobsByProjectIdQuery,
  useLazyReadJobsByProjectIdQuery,
  useReadJobsByProjectIdsQuery,
  useLazyReadJobsByProjectIdsQuery,
  useReadLatestJobsByProjectIdsQuery,
  useReadVersionOriginJobsQuery,
  useReadPipelineQuery,
  useTriggerPipelineExecutionMutation,
  useUpdatePipelineConfigurationMutation,
  useCreateConversionMutation,
  useReadJobQuery,
  useLazyReadJobQuery,
  useReadJobLogsQuery,
  useDeleteJobMutation,
  useDeletePipelineExecutionMutation,
  useDownloadArtifactsForJobMutation,
  useReadPipelineNamesForPolicyRowQuery,
  useCreateDeviceJobMutation,
  useCreateJobMutation,
  useReadPipelineExecutionQuery,
  useReadGroupedPipelinesCountQuery,
  useReadGroupedPipelineCountsQuery,
  useReadLatestExecutionsForPipelinesQuery,
  useReadUngroupedPipelinesCountQuery,
  useReadDeploymentRequestQuery,
  useReadDeploymentRequestsQuery,
  useCreateDeploymentRequestMutation,
  useApproveDeploymentRequestMutation,
  useDenyDeploymentRequestMutation,
  useCancelDeploymentRequestMutation,
  useExecuteDeploymentRequestMutation,
  useDeleteDeploymentRequestMutation,
} = jobsApi;
